import { KeyboardArrowLeft, KeyboardArrowRight, VisibilityOutlined } from '@mui/icons-material';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Scrollbar } from 'src/components/scrollbar';
import { paths } from 'src/paths';
import { Permissions, useScopeChecker } from 'src/rbac';
import { checkPaperSignatureEligibility } from 'src/utils/signaturePermissions';

type Props = {
  physicianSignatureDoc: any;
  activeStep: any;
  handleNextSelectPhysician: any;
  handleBack: any;
  setSelectedDocument: any;
  selectedDocument: any;
  handelNavigateBack: any;
  selectedAttachmentType: any;
};

const PhysicianDocTable = (props: Props) => {
  const {
    physicianSignatureDoc,
    activeStep,
    handleNextSelectPhysician,
    handleBack,
    setSelectedDocument,
    selectedDocument,
    handelNavigateBack,
    selectedAttachmentType,
  } = props;
  const navigate = useNavigate();
  const { state } = useLocation();
  const { patients, patientDetails, episodeOfCare, details, previewDocument } = paths;
  const getAllSubmittedDocumentResponse = useSelector(
    (state: any) => state.getAllSubmittedDocumentReducer
  );
  const has = useScopeChecker();
  const episodeOfCareId = state?.episodeOfCareId;
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const checkReadPermission = (document: any) => {
    if (
      document.formType?.includes('RECERTIFICATION') &&
      !has(Permissions.READ_RECERTIFICATION)
    ) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !has(Permissions.READ_EVAL)) {
      return false;
    }
    return true;
  };

  const handleSelectDocument = (e: any, document: any) => {
    if (e.target.checked) {
      setSelectedDocument(document);
    } else {
      setSelectedDocument('');
    }
  };

  const setPreviewDocument = (document: any) => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${previewDocument}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
        source: 'physician-sign',
      },
    });
  };


  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
        p={1}
      >
        <Box
          sx={{ border: '1px solid #F2F4F7', borderRadius: '20px', width: '100%' }}
          m={3}
        >
          <Card>
            <Grid
              container
              sx={{ padding: '18px 24px 18px 24px', position: 'relative' }}
            >
              <Typography variant="h6">Documents</Typography>

              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.26)',
                  borderRadius: '12px',
                  padding: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '12px',
                }}
              >
                <Typography sx={{ color: '#111927', fontSize: '13px', letterSpacing: '0.16px' }}>
                  {`${physicianSignatureDoc?.length ? physicianSignatureDoc?.length : 0} Document`}
                </Typography>
              </Box>
              {/* )} */}
            </Grid>
            <Divider />
            <Scrollbar>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Document Type</TableCell>
                    <TableCell>Date of Service</TableCell>
                    <TableCell>Therapist Signature</TableCell>
                    <TableCell>Physician Signature</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAllSubmittedDocumentResponse?.loader ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box
                          textAlign="center"
                          p={3}
                        >
                          <Skeleton animation="wave" />
                          <Skeleton animation="wave" />
                          <Skeleton animation="wave" />
                          <Skeleton animation="wave" />
                          <Skeleton animation="wave" />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : physicianSignatureDoc?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box
                          textAlign="center"
                          p={3}
                        >
                          <Typography
                            color={'text.secondary'}
                            variant="subtitle2"
                          >
                            No Records Found
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    physicianSignatureDoc?.map((document: any) => {
                      // THIN-7550: gray out documents that aren't eligible for the selected wet-signature
                      // type yet (FSM ordering — e.g. supervisor sig before the therapist has signed).
                      const { eligible, reason } = checkPaperSignatureEligibility(
                        document,
                        selectedAttachmentType
                      );
                      return (
                      <TableRow key={document.uuid}>
                        <TableCell>
                          <Grid
                            container
                            alignItems="center"
                            direction="row"
                          >
                            <Checkbox
                              checked={selectedDocument?.uuid === document?.uuid ? true : false}
                              disabled={!eligible}
                              onChange={(e) => handleSelectDocument(e, document)}
                            />

                            <Box>
                              <Typography
                                variant="subtitle2"
                                color={eligible ? undefined : 'text.disabled'}
                                sx={{ marginLeft: '4px' }}
                              >
                                {document.documentType}
                              </Typography>
                              {!eligible && reason ? (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ marginLeft: '4px', display: 'block' }}
                                >
                                  {reason}
                                </Typography>
                              ) : null}
                            </Box>
                          </Grid>
                        </TableCell>
                        <TableCell>
                          {document.dateOfService
                            ? format(
                                new Date(
                                  new Date(document.dateOfService).valueOf() +
                                    new Date(document.dateOfService).getTimezoneOffset() * 60 * 1000
                                ),
                                'dd MMM, yyyy'
                              )
                            : '-'}
                        </TableCell>
                        {/* THIN-7286: read signer names per axis from signatureContext.
                            Therapist cell previously read signedBy[0]; Physician cell
                            previously read supervisorName (which was overloaded with the
                            physician's name on Eval/Recerts due to the BE's
                            resolveSecondarySignerName). signatureContext carries one name
                            per axis. */}
                        <TableCell>
                          <Typography
                            variant="caption"
                            display={'inline'}
                          >
                            {document?.signatureContext?.therapist?.name ? (
                              document.signatureContext.therapist.name
                            ) : (
                              <Link>
                                <Typography
                                  variant="body2"
                                  sx={{ cursor: 'pointer' }}
                                >
                                  Re-assign Therapist
                                </Typography>
                              </Link>
                            )}
                          </Typography>

                          {document?.signatureContext?.therapist?.name &&
                            !document?.isReassigned &&
                            document?.status !== 'COMPLETED' && (
                              <Link>
                                <Typography
                                  variant="body2"
                                  sx={{ cursor: 'pointer' }}
                                >
                                  Re-assign Therapist
                                </Typography>
                              </Link>
                            )}
                        </TableCell>

                        <TableCell>
                          <Typography
                            variant="caption"
                            display={'inline'}
                          >
                            {document?.signatureContext?.physician?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {document.pdfUrl && checkReadPermission(document) && (
                            <IconButton onClick={() => setPreviewDocument(document)}>
                              <VisibilityOutlined />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </Card>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box>
            <Button
              variant="outlined"
              disabled={activeStep === 0}
              startIcon={<KeyboardArrowLeft />}
              onClick={handleBack}
            >
              Back
            </Button>
          </Box>
          <Box>
            <Button
              sx={{ mr: 2, color: '#384250' }}
              onClick={handelNavigateBack}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              //   type="submit"
              endIcon={<KeyboardArrowRight />}
              onClick={handleNextSelectPhysician}
              disabled={
                !selectedDocument?.uuid &&
                (selectedAttachmentType === 'PhysicianSignature' ||
                  selectedAttachmentType === 'SUPERVISOR_SIGNATURE')
              }
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PhysicianDocTable;
